import React, { useCallback, useContext, useState } from 'react';
import { useAjax } from './use-ajax';
import { useCache } from './use-cache';

interface IAuthData {
	userId?: number;
	accessToken?: string;
	refreshToken?: string;
	userName?: string;
	isLogin: boolean;
}

interface IAuthContext extends Partial<IAuthData> {
	setAuthData?: React.Dispatch<React.SetStateAction<IAuthData | null>>;
	isLogin?: boolean;
}

const AuthContext = React.createContext<IAuthContext>({});

function useLogin() {
	const { setAuthData } = useContext(AuthContext);
	const [, setAuthCache] = useCache<IAuthData>('authCache');

	return useAjax<{
		code: number;
		data: IAuthData;
	}>({
		url: '/api/manage/auth/signIn',
		method: 'post',
		errMsg: '登入失敗',
		errCode: {
			490: '帳號或密碼錯誤',
			491: 'AD驗證失敗',
			492: '您所使用的IP位址沒有權限訪問此系統，請更換IP位址或連絡帳號管理者，謝謝!',
			493: '帳號狀態異常(鎖定、關閉、未認證...)，請聯絡系統管理者',
			494: '登入錯誤太多次，帳號已鎖定',
			495: '帳號權限異常或沒有權限',
		},
		onSuccess(result) {
			setAuthData?.({ ...result.data, isLogin: true });
			setAuthCache(result.data);
		},
	});
}

function useLogout() {
	const { setAuthData } = useAuth();
	const [authCache, setAuthCache] = useAuthCache();
	// const isOnLogout = useRef(false);

	const [_logout] = useAjax({
		url: '/api/manage/auth/signOut',
		method: 'post',
		data: {
			refreshToken: authCache?.refreshToken,
		},
		onSuccess(result) {
			// onLogout.current = false;
			// setAuthData?.({ isLogin: false });
		},
		onError(xhr) {
			// onLogout.current = false;
			// setAuthData?.({ isLogin: false });
		},
	});

	const logout = useCallback(() => {
		_logout();
		setAuthData?.({ isLogin: false });
		setAuthCache?.(null);
		// if (!isOnLogout.current && isLogin) {
		// 	isOnLogout.current = true;
		// 	_logout();
		// 	setAuthData?.({ isLogin: false });
		// 	setAuthCache?.(null);
		// 	setTimeout(() => {
		// 		isOnLogout.current = false;
		// 	}, 1500);
		// }
	}, [_logout, setAuthData, setAuthCache]);

	return logout;
}

function useAuth() {
	return useContext(AuthContext);
}

function useAuthCache() {
	return useCache<IAuthData>('authCache');
}

const AuthProvider: React.FC<{
	children: (authData: IAuthData | null) => JSX.Element;
}> = ({ children }) => {
	const [authCache] = useAuthCache();
	const [authData, setAuthData] = useState<IAuthData | null>(() => {
		if (authCache) {
			return { ...authCache, isLogin: true };
		}
		return { isLogin: false };
	});

	return <AuthContext.Provider value={{ ...authData, setAuthData }}>{children(authData)}</AuthContext.Provider>;
};

export { useAuth, useLogin, AuthProvider, useLogout, useAuthCache };
